import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { authApi, usageApi } from '../api/client'
import storage from '../utils/storage'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(storage.get('pindou_token'))
  const [loading, setLoading] = useState(true)

  const isPremiumActive = Boolean(
    user?.isPremium && Number(user?.premiumUntil) > Date.now()
  )

  const normalizeUser = (u) => ({
    ...u,
    username: u.username || null,
    exportsUsed: u.exportsUsed ?? 0,
    exportsLimit: u.exportsLimit ?? 1,
    exportsResetAt: u.exportsResetAt ?? 0,
    quota: u.quota ?? 0,
    quotaExpiresAt: u.quotaExpiresAt ?? 0,
    checkinStreak: u.checkinStreak ?? 0,
    lastCheckinAt: u.lastCheckinAt ?? 0,
    isAdmin: Boolean(u.isAdmin),
    isMerchant: Boolean(u.isMerchant),
  })

  const refreshUsage = useCallback(async () => {
    if (!token) return
    try {
      const { data } = await usageApi.me()
      setUser((prev) =>
        prev
          ? normalizeUser({
              ...prev,
              exportsUsed: data.exportsUsed,
              exportsLimit: data.exportsLimit,
              exportsResetAt: data.exportsResetAt,
              quota: data.aiQuota,
              quotaExpiresAt: data.quotaExpiresAt ?? 0,
              checkinStreak: data.checkinStreak,
              lastCheckinAt: data.lastCheckinAt,
              isPremium: data.isPremium,
            })
          : null
      )
    } catch (err) {
      console.warn('refresh usage failed', err)
    }
  }, [token])

  useEffect(() => {
    const storedUser = storage.get('pindou_user')
    if (storedUser) {
      try {
        setUser(normalizeUser(JSON.parse(storedUser)))
      } catch {
        storage.remove('pindou_user')
      }
    }

    const init = async () => {
      if (token) {
        try {
          const { data } = await authApi.me()
          const u = normalizeUser(data.user || data)
          setUser(u)
          storage.set('pindou_user', JSON.stringify(u))
          // 同步最新用量
          await refreshUsage()
        } catch {
          logout(false)
        }
      }
      setLoading(false)
    }

    init()
  }, [token])

  const login = async (email, password) => {
    const { data } = await authApi.login(email, password)
    const t = data.token
    const u = normalizeUser(data.user || data)
    storage.set('pindou_token', t)
    storage.set('pindou_user', JSON.stringify(u))
    setToken(t)
    setUser(u)
    // 同步用量
    try {
      const { data: usage } = await usageApi.me()
      setUser((prev) =>
        normalizeUser({
          ...u,
          exportsUsed: usage.exportsUsed,
          exportsLimit: usage.exportsLimit,
          exportsResetAt: usage.exportsResetAt,
          quota: usage.aiQuota,
          checkinStreak: usage.checkinStreak,
          lastCheckinAt: usage.lastCheckinAt,
          isPremium: usage.isPremium,
        })
      )
    } catch {
      // ignore
    }
    return u
  }

  const register = async (email, password, username, activationCode, role) => {
    const { data } = await authApi.register(email, password, username, activationCode, role)
    const t = data.token
    const u = normalizeUser(data.user || data)
    storage.set('pindou_token', t)
    storage.set('pindou_user', JSON.stringify(u))
    setToken(t)
    setUser(u)
    return u
  }

  const updateUser = (updates) => {
    setUser((prev) => {
      if (!prev) return null
      const next = normalizeUser({ ...prev, ...updates })
      storage.set('pindou_user', JSON.stringify(next))
      return next
    })
  }

  const logout = (navigate = true) => {
    storage.remove('pindou_token')
    storage.remove('pindou_user')
    setToken(null)
    setUser(null)
    if (navigate) window.location.href = '/login'
  }

  return (
    <AuthContext.Provider
      value={{ user, token, isPremium: isPremiumActive, loading, login, register, updateUser, logout, refreshUsage }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
